import React, { useContext, useState, useCallback, useEffect, useRef } from "react";
import { FilterContext } from "../../../context/Filter";
import {
  fetchlobListJcDetails,
  fetchplListJcDetails,
  fetchPplListJcDetails,
  fetchstateListJcDetails,
  fetchyearListJcDetails,
} from "./Jckpi";

const JcFilter = () => {
  const {
    arn: parArn,
    childArn,
    jcDropDownPayload,
    setJcDropDownPayload,
    jcPayload,
    setJcPayload,
  } = useContext(FilterContext);

  const [states, setStates] = useState([]);
  const [years, setYears] = useState([]);
  const [lob, setLob] = useState([]);
  const [ppl, setPpl] = useState([]);
  const [pl, setPl] = useState([]);

  const [loading, setLoading] = useState(true);
  const [updatingDropdown, setUpdatingDropdown] = useState(null);

  const [selectedStates, setSelectedStates] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedLob, setSelectedLob] = useState("");
  const [selectedPpl, setSelectedPpl] = useState("");
  const [selectedPl, setSelectedPl] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const syncInProgress = useRef(false);

  // Static status options
  const statusOptions = [
    { key: "Open", label: "Open", value: "Open" },
    { key: "Closed", label: "Close", value: "Closed" }
  ];

  useEffect(() => {
    if (jcDropDownPayload && !syncInProgress.current) {
      syncInProgress.current = true;

      setSelectedStates(jcDropDownPayload.state?.[0] || "");
      setSelectedYear(jcDropDownPayload.fiscalYear?.[0] || "");
      setSelectedLob(jcDropDownPayload.lob?.[0] || "");
      setSelectedPpl(jcDropDownPayload.ppl?.[0] || "");
      setSelectedPl(jcDropDownPayload.pl?.[0] || "");
      setSelectedStatus(jcDropDownPayload.status || "");

      setTimeout(() => {
        syncInProgress.current = false;
      }, 100);
    }
  }, [jcDropDownPayload]);

  const generatePayload = useCallback(() => {
    const payload = { ...(childArn ? { childArn } : { parArn }) };

    if (selectedStates) payload.state = [selectedStates];
    if (selectedYear) payload.fiscalYear = [selectedYear];
    if (selectedLob) payload.lob = [selectedLob];
    if (selectedPpl) payload.ppl = [selectedPpl];
    if (selectedPl) payload.pl = [selectedPl];
    if (selectedStatus) payload.status = selectedStatus;

    return payload;
  }, [parArn, childArn, selectedStates, selectedYear, selectedLob, selectedPpl, selectedPl, selectedStatus]);

  const updateAllDropdowns = useCallback(
    async (changedField, newValue) => {
      setUpdatingDropdown("all");

      const currentPayload = { ...(childArn ? { childArn } : { parArn }) };

      if (changedField === "state" && newValue) currentPayload.state = [newValue];
      else if (selectedStates) currentPayload.state = [selectedStates];

      if (changedField === "fiscalYear" && newValue) currentPayload.fiscalYear = [newValue];
      else if (selectedYear) currentPayload.fiscalYear = [selectedYear];

      if (changedField === "lob" && newValue) currentPayload.lob = [newValue];
      else if (selectedLob) currentPayload.lob = [selectedLob];

      if (changedField === "ppl" && newValue) currentPayload.ppl = [newValue];
      else if (selectedPpl) currentPayload.ppl = [selectedPpl];

      if (changedField === "pl" && newValue) currentPayload.pl = [newValue];
      else if (selectedPl) currentPayload.pl = [selectedPl];

      if (changedField === "status" && newValue) currentPayload.status = newValue;
      else if (selectedStatus) currentPayload.status = selectedStatus;

      try {
        const [stateData, yearData, lobData, pplData, plData] = await Promise.all([
          fetchstateListJcDetails(parArn, childArn, currentPayload),
          fetchyearListJcDetails(parArn, childArn, currentPayload),
          fetchlobListJcDetails(parArn, childArn, currentPayload),
          fetchPplListJcDetails(parArn, childArn, currentPayload),
          fetchplListJcDetails(parArn, childArn, currentPayload),
        ]);

        setStates(stateData || []);
        setYears(yearData || []);
        setLob(lobData || []);
        setPpl(pplData || []);
        setPl(plData || []);

        validateAndResetSelections([stateData, yearData, lobData, pplData, plData], changedField);
      } catch (error) {
        console.error(`Failed to update all dropdowns after ${changedField} change:`, error);
      } finally {
        setUpdatingDropdown(null);
      }
    },
    [parArn, childArn, selectedStates, selectedYear, selectedLob, selectedPpl, selectedPl, selectedStatus]
  );

  const validateAndResetSelections = useCallback(
    (results, changedField) => {
      const [stateData, yearData, lobData, pplData, plData] = results;

      if (
        changedField !== "state" &&
        stateData &&
        selectedStates &&
        !stateData.some((item) => (item.key || item.value) === selectedStates)
      ) {
        setSelectedStates("");
      }

      if (
        changedField !== "fiscalYear" &&
        yearData &&
        selectedYear &&
        !yearData.some((item) => (item.key || item.value) === selectedYear)
      ) {
        setSelectedYear("");
      }

      if (
        changedField !== "lob" &&
        lobData &&
        selectedLob &&
        !lobData.some((item) => (item.key || item.value) === selectedLob)
      ) {
        setSelectedLob("");
      }

      if (
        changedField !== "ppl" &&
        pplData &&
        selectedPpl &&
        !pplData.some((item) => (item.key || item.value) === selectedPpl)
      ) {
        setSelectedPpl("");
      }

      if (
        changedField !== "pl" &&
        plData &&
        selectedPl &&
        !plData.some((item) => (item.key || item.value) === selectedPl)
      ) {
        setSelectedPl("");
      }
    },
    [selectedStates, selectedYear, selectedLob, selectedPpl, selectedPl]
  );

  const handleStateChange = useCallback((value) => {
    setSelectedStates(value);
    if (value) updateAllDropdowns("state", value);
  }, [updateAllDropdowns]);

  const handleYearChange = useCallback((value) => {
    setSelectedYear(value);
    if (value) updateAllDropdowns("fiscalYear", value);
  }, [updateAllDropdowns]);

  const handleLobChange = useCallback((value) => {
    setSelectedLob(value);
    if (value) updateAllDropdowns("lob", value);
  }, [updateAllDropdowns]);

  const handlePplChange = useCallback((value) => {
    setSelectedPpl(value);
    if (value) updateAllDropdowns("ppl", value);
  }, [updateAllDropdowns]);

  const handlePlChange = useCallback((value) => {
    setSelectedPl(value);
    if (value) updateAllDropdowns("pl", value);
  }, [updateAllDropdowns]);

  const handleStatusChange = useCallback((value) => {
    setSelectedStatus(value);
    // Note: Status doesn't trigger dropdown updates since it's a static field
  }, []);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (isSubmitting || syncInProgress.current) return;

      setIsSubmitting(true);
      syncInProgress.current = true;

      const payload = generatePayload();

      setTimeout(() => {
        setJcDropDownPayload(payload);
        setJcPayload(payload);

        setTimeout(() => {
          setIsSubmitting(false);
          syncInProgress.current = false;
        }, 500);
      }, 0);
    },
    [generatePayload, isSubmitting, setJcDropDownPayload, setJcPayload]
  );

  const handleReset = useCallback(
    (e) => {
      if (e?.preventDefault) e.preventDefault();
      if (syncInProgress.current) return;

      syncInProgress.current = true;

      const resetPayload = childArn ? { childArn } : { parArn };

      setSelectedStates("");
      setSelectedYear("");
      setSelectedLob("");
      setSelectedPpl("");
      setSelectedPl("");
      setSelectedStatus("");

      fetchFilters();

      setTimeout(() => {
        setJcDropDownPayload(resetPayload);
        setJcPayload(resetPayload);
        setTimeout(() => {
          syncInProgress.current = false;
        }, 500);
      }, 0);
    },
    [parArn, childArn, setJcDropDownPayload, setJcPayload]
  );

  const hasInitialized = useRef(false);

  const fetchFilters = useCallback(async () => {
    setLoading(true);
    const payload = childArn ? { childArn } : { parArn };

    try {
      const [stateData, yearData, lobData, pplData, plData] = await Promise.all([
        fetchstateListJcDetails(parArn, childArn, payload),
        fetchyearListJcDetails(parArn, childArn, payload),
        fetchlobListJcDetails(parArn, childArn, payload),
        fetchPplListJcDetails(parArn, childArn, payload),
        fetchplListJcDetails(parArn, childArn, payload),
      ]);

      setStates(stateData || []);
      setYears(yearData || []);
      setLob(lobData || []);
      setPpl(pplData || []);
      setPl(plData || []);
    } catch (err) {
      console.error("Failed to fetch JC filters", err);
    } finally {
      setLoading(false);
    }
  }, [parArn, childArn]);

  useEffect(() => {
    if ((parArn || childArn) && !hasInitialized.current) {
      hasInitialized.current = true;
      fetchFilters();
    }
  }, [parArn, childArn, fetchFilters]);

  if (loading) {
    return (
      <div className="max-w-full mx-auto p-6 bg-white rounded-md shadow-md flex items-center justify-center">
        <div className="text-gray-500">Loading filters...</div>
      </div>
    );
  }

  return (
    <div className="max-w-full ml-4 mx-auto flex flex-wrap justify-center gap-x-4 gap-y-2 p-3 mt-2 mb-1 bg-white rounded-md shadow-md">
      <Dropdown label="Status" value={selectedStatus} onChange={handleStatusChange} options={statusOptions} loading={false} />
      <Dropdown label="State" value={selectedStates} onChange={handleStateChange} options={states} loading={updatingDropdown === "all"} />
      <Dropdown label="Year" value={selectedYear} onChange={handleYearChange} options={years} loading={updatingDropdown === "all"} />
      <Dropdown label="LOB" value={selectedLob} onChange={handleLobChange} options={lob} loading={updatingDropdown === "all"} />
      <Dropdown label="PPL" value={selectedPpl} onChange={handlePplChange} options={ppl} loading={updatingDropdown === "all"} />
      <Dropdown label="PL" value={selectedPl} onChange={handlePlChange} options={pl} loading={updatingDropdown === "all"} />
      
      <div className="flex items-end">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting || updatingDropdown}
          className={`px-2 py-1 rounded-md transition ${
            isSubmitting || updatingDropdown
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-[#2374BF] hover:bg-indigo-700"
          } text-white`}
        >
          {isSubmitting ? "Submitting..." : "Submit"}
        </button>
      </div>
      <div className="flex items-end">
        <button
          type="button"
          onClick={handleReset}
          disabled={isSubmitting || updatingDropdown}
          className="bg-gray-400 text-white px-4 py-1 rounded-md hover:bg-gray-700 transition disabled:cursor-not-allowed"
        >
          Reset
        </button>
      </div>
    </div>
  );
};

const Dropdown = React.memo(({ label, value, onChange, options, loading = false }) => {
  return (
    <div className="flex gap-2 items-center text-xs">
      <label>{label}:</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={loading}
        className={`border border-gray-800 rounded-md py-1 px-2 ${
          loading ? "bg-gray-100 cursor-not-allowed" : ""
        }`}
      >
        <option value="">
          {loading ? `Loading ${label}...` : `Select ${label}`}
        </option>
        {options?.map((item) => (
          <option key={item.key || item.value} value={item.key || item.value}>
            {item.label || item.key || item.value}
          </option>
        ))}
      </select>
    </div>
  );
});

Dropdown.displayName = "Dropdown";

export default JcFilter;